import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { corpCookies } from '@/utils/cookies'

const OTP_EXPIRES_IN_SECONDS = 600 // 10 minutos

export const useCorpSessionStore = defineStore('corp-session', () => {
  // Challenge state (antes do OTP)
  const challengeToken = ref('')
  const challengeExpiresIn = ref(OTP_EXPIRES_IN_SECONDS)
  const otpSentTo = ref('')
  const challengeCreatedAt = ref<string | null>(null)
  
  // Session state (após OTP)
  const accessToken = ref('')
  const refreshToken = ref('')
  const accountId = ref<string | null>(null)
  const businessCnpj = ref<string | null>(null)
  const expiresAt = ref<string | null>(null)
  
  // Inicializa dos cookies
  const initFromCookies = () => {
    const session = corpCookies.getSession()
    const challenge = corpCookies.getChallenge()
    
    if (challenge?.challengeToken) {
      challengeToken.value = challenge.challengeToken
      challengeExpiresIn.value = challenge.expiresIn
      otpSentTo.value = challenge.otpSentTo
      challengeCreatedAt.value = challenge.createdAt
    }
    
    if (session?.accessToken) {
      accessToken.value = session.accessToken
      refreshToken.value = session.refreshToken
      accountId.value = session.accountId
      businessCnpj.value = session.businessCnpj ?? null
      expiresAt.value = session.expiresAt
    }
  }
  
  // Inicializa na criação do store
  initFromCookies()
  
  // Getters
  const hasSession = computed(() => !!(accessToken.value && refreshToken.value))
  const hasChallenge = computed(() => !!challengeToken.value)
  
  const isChallengeValid = computed(() => {
    if (!challengeToken.value || !challengeCreatedAt.value) return false
    const createdAt = new Date(challengeCreatedAt.value)
    const expires = new Date(createdAt.getTime() + challengeExpiresIn.value * 1000)
    return expires > new Date()
  })
  
  const isSessionValid = computed(() => {
    if (!accessToken.value || !expiresAt.value) return false
    return new Date(expiresAt.value) > new Date()
  })
  
  const otpTimeRemaining = computed(() => {
    if (!challengeCreatedAt.value) return 0
    const createdAt = new Date(challengeCreatedAt.value)
    const expires = new Date(createdAt.getTime() + challengeExpiresIn.value * 1000)
    const remaining = expires.getTime() - Date.now()
    return Math.max(0, Math.floor(remaining / 1000))
  })
  
  // Actions
  const setChallenge = (token: string, expiresIn: number, sentTo: string) => {
    challengeToken.value = token
    challengeExpiresIn.value = expiresIn
    otpSentTo.value = sentTo
    challengeCreatedAt.value = new Date().toISOString()
    
    corpCookies.setChallenge(token, expiresIn, sentTo)
  }
  
  const resetOtpTimer = () => {
    challengeCreatedAt.value = new Date().toISOString()
    
    if (challengeToken.value) {
      corpCookies.setChallenge(
        challengeToken.value,
        challengeExpiresIn.value,
        otpSentTo.value
      )
    }
  }
  
  const setSession = (
    access: string,
    refresh: string,
    account: string | null,
    expiresIn: number,
    cnpj?: string | null
  ) => {
    accessToken.value = access
    refreshToken.value = refresh
    accountId.value = account
    businessCnpj.value = cnpj ?? null
    expiresAt.value = new Date(Date.now() + expiresIn * 1000).toISOString()
    
    // Limpa challenge
    challengeToken.value = ''
    challengeExpiresIn.value = OTP_EXPIRES_IN_SECONDS
    otpSentTo.value = ''
    challengeCreatedAt.value = null
    
    corpCookies.setSession(access, refresh, account, expiresIn, cnpj)
  }
  
  const updateTokens = (access: string, refresh: string, expiresIn: number) => {
    accessToken.value = access
    refreshToken.value = refresh
    expiresAt.value = new Date(Date.now() + expiresIn * 1000).toISOString()
    
    corpCookies.updateTokens(access, refresh, expiresIn)
  }
  
  const clearSession = () => {
    challengeToken.value = ''
    challengeExpiresIn.value = OTP_EXPIRES_IN_SECONDS
    otpSentTo.value = ''
    challengeCreatedAt.value = null
    accessToken.value = ''
    refreshToken.value = ''
    accountId.value = null
    businessCnpj.value = null
    expiresAt.value = null
    
    corpCookies.clearAll()
  }
  
  const syncFromCookies = () => {
    initFromCookies()
  }
  
  return {
    // State
    challengeToken,
    challengeExpiresIn,
    otpSentTo,
    challengeCreatedAt,
    accessToken,
    refreshToken,
    accountId,
    businessCnpj,
    expiresAt,
    
    // Getters
    hasSession,
    hasChallenge,
    isChallengeValid,
    isSessionValid,
    otpTimeRemaining,
    
    // Actions
    setChallenge,
    resetOtpTimer,
    setSession,
    updateTokens,
    clearSession,
    syncFromCookies,
  }
}, {
  persist: {
    key: 'ecx-corp-session',
    storage: sessionStorage,
  },
})
